import { useMemo } from 'react';
import type { PpaMetadataField } from 'utils/ppaHelpers';

export type PpaScenarioMetadataField = PpaMetadataField;

export type PpaScenarioMetadataSummary = {
    fields: PpaScenarioMetadataField[];
};

// Labels that should display their value as-is (not converted to a count).
const NON_COUNT_LABELS = new Set(['Source', 'Simulation Period']);

const SUMMARY_EXCLUDED_LABELS = new Set(['Project Visibility']);

function countCommaSeparatedValues(value: string): string {
    return String(
        value
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean).length,
    );
}

// Derives "Channel(s)" from "Channels", "Category(s)" from "Categories", etc.
function toCountLabel(pluralLabel: string): string {
    if (pluralLabel.endsWith('ies')) {
        return `${pluralLabel.slice(0, -3)}y(s)`;
    }
    if (pluralLabel.endsWith('s')) {
        return `${pluralLabel.slice(0, -1)}(s)`;
    }
    return `${pluralLabel}(s)`;
}

export function toMetadataSummaryFields(
    fields: PpaScenarioMetadataField[],
): PpaScenarioMetadataField[] {
    return fields
        .filter((field) => !SUMMARY_EXCLUDED_LABELS.has(field.label))
        .map((field) => {
            if (NON_COUNT_LABELS.has(field.label)) {
                return field;
            }
            return {
                label: toCountLabel(field.label),
                value: countCommaSeparatedValues(field.value),
            };
        });
}

export function usePpaScenarioMetadataSummary(
    dynamicFields?: PpaScenarioMetadataField[] | null,
): PpaScenarioMetadataSummary {
    return useMemo(
        () => ({
            fields: dynamicFields ?? [],
        }),
        [dynamicFields],
    );
}
